import React from 'react';
import { Container } from '@ui';
import { FlatButton } from '@ui/buttons';
import { FormState } from '@ui/FormState';
import { ProgressOverlay } from '@ui/ProgressOverlay';
import { sendMultipartApiRequest } from '@services/api';
import { handle400Statuses } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import { FileDragAndDrop } from './FileDragAndDrop';

function UploadingOverlay() {
  return <ProgressOverlay message="Uploading..." />;
}

const TEMPLATE_ALLOWED_TYPES = ['pdf', 'docx', 'pptx'];

function buildFormData(file) {
  const formData = new FormData();
  const fieldName = file.type === 'pdf' ? 'pdf' : file.type;
  formData.append(fieldName, file.file);
  formData.append('name', file.name);
  formData.append('generate_template_spec', true);
  return formData;
}

export function UploadPhase({ onUploadComplete }) {
  const [files, setFiles] = React.useState([]);
  const [isUploading, setIsUploading] = React.useState(false);
  const [formError, setFormError] = React.useState(null);

  const handleAddFiles = React.useCallback((incoming) => {
    setFiles((prev) => {
      const existingNames = new Set(prev.map((f) => `${f.name}.${f.type}`));
      const duplicates = incoming.filter((f) => existingNames.has(`${f.name}.${f.type}`));
      if (duplicates.length > 0) {
        const names = duplicates.map((f) => `${f.name}.${f.type}`).join(', ');
        setFormError(`${names} ${duplicates.length > 1 ? 'are' : 'is'} already awaiting your upload submission.`);
        return prev;
      }
      setFormError(null);
      return [...prev, ...incoming];
    });
  }, []);

  const handleRemoveFile = React.useCallback((fileToRemove) => {
    setFiles((prev) => prev.filter((f) => f.name !== fileToRemove.name));
  }, []);

  const handleRenameFile = React.useCallback((file, newName) => {
    setFiles((prev) => prev.map((f) => (f.name === file.name ? { ...f, name: newName } : f)));
  }, []);

  const handleUpload = React.useCallback(async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setFormError(null);

    try {
      const uploadResults = await Promise.all(
        files.map(async (file) => {
          const formData = buildFormData(file);
          const { status, data, error } = await sendMultipartApiRequest('POST', 'v1/genai/upload/ms', formData);
          logNetRequest(status, data);
          handle400Statuses(status);
          return { status, data, error, fileName: file.name };
        })
      );

      const failed = uploadResults.filter((r) => r.status !== 200);
      if (failed.length > 0) {
        setFormError(`${failed.length} file${failed.length > 1 ? 's' : ''} failed to upload. Please try again.`);
      } else {
        onUploadComplete();
      }
    } catch (e) {
      setFormError('Something went wrong uploading. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [files, onUploadComplete]);

  return (
    <Container>
      {isUploading && <UploadingOverlay />}
      <Container top={24}>
        <FileDragAndDrop
          files={files}
          onAddFiles={handleAddFiles}
          onRemoveFile={handleRemoveFile}
          onRenameFile={handleRenameFile}
          allowedTypes={TEMPLATE_ALLOWED_TYPES}
        />
      </Container>
      <Container top={16} centerAll>
        <FormState error={formError} />
      </Container>
      <Container top={24} style={{ textAlign: 'center' }}>
        <FlatButton fullWidth disabled={files.length === 0 || isUploading} onClick={handleUpload}>
          {files.length > 1 ? `Upload ${files.length} Files` : 'Upload File'}
        </FlatButton>
      </Container>
    </Container>
  );
}
